import { segment } from '@actions/analytics';
import { evtNameCategoryMap } from '@constants/index';
import useFetch from '@hooks/useFetch';
import useFirstRenderSkip from '@hooks/useFirstRenderSkip';
import useInterval from '@hooks/useInterval';
import isEmpty from 'lodash/isEmpty';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  DST_MAX_POLLING_ATTEMPTS,
  DST_POLLING_INTERVAL,
  DST_STATES
} from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/utils';
import { getDSTEndpoint } from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/apiHelper';
import { SCHOLARSHIP_TEST_CARD_VIEWED } from '@cont/SeeAll/TestSeriesListing/DailyScholarshipTest/constants/segment';
import useMyInfo from '@api/hooks/auth/useMyInfo';
import { getIsLoggedIn } from '@api/selectors/auth';
import {
  getIsSubscribedToGoal,
  getIsSubscribedToIconic,
  getIsSubscribedToIconicPro,
  getIsSubscribedToLite
} from '@api/selectors/subscription';

function useDst(
  goalUID,
  goalName,
  lps,
  options = { fallbackData: undefined, revalidateOnMount: true }
) {
  const isIconicUser = useMyInfo(getIsSubscribedToIconic(goalUID));
  const isIconicProUser = useMyInfo(getIsSubscribedToIconicPro(goalUID));
  const isPlusUser = useMyInfo(getIsSubscribedToGoal(goalUID));
  const isLiteActive = useMyInfo(getIsSubscribedToLite(goalUID));
  const isSubscribedToGoal =
    isIconicUser || isIconicProUser || isPlusUser || isLiteActive;
  const key = isSubscribedToGoal ? null : getDSTEndpoint(goalUID);
  const { data, mutate, ...rest } = useFetch(
    key,
    {},
    {
      fallbackData: options.fallbackData,
      revalidateOnMount: options.revalidateOnMount
    }
  );
  const [noOfAttempts, setNoOfAttempts] = useState(0);

  const isLoggedIn = useMyInfo(getIsLoggedIn);
  useFirstRenderSkip(() => {
    if (isLoggedIn) {
      mutate();
    }
  }, [isLoggedIn, mutate]);

  useEffect(() => {
    if (
      !isEmpty(data) &&
      [DST_STATES.UNATTEMPTED, DST_STATES.RESUME, DST_STATES.REATTEMPT].indexOf(
        data.data[0].state
      ) > -1
    ) {
      segment.track(
        SCHOLARSHIP_TEST_CARD_VIEWED,
        {
          last_primary_source: lps,
          goal_name: goalName,
          goal_uid: goalUID,
          scholarship_test_state: data.data[0].state,
          is_reattempt: data.data[0].state === DST_STATES.REATTEMPT,
          is_resumed: data.data[0].state === DST_STATES.RESUME,
          use_segment_api: true
        },
        evtNameCategoryMap.WITHOUT_CATEGORY
      );
    }
  }, [data, lps, goalName, goalUID]);

  const checkForTerminalState = useCallback(() => {
    if (!isEmpty(data) && data.data[0].state === DST_STATES.ATTEMPTED) {
      mutate();
      setNoOfAttempts(noOfAttempts + 1);
    }
  }, [data, mutate, noOfAttempts]);

  const shouldPoll = useMemo(() => {
    return (
      !isEmpty(data) &&
      data.data[0].state === DST_STATES.ATTEMPTED &&
      noOfAttempts < DST_MAX_POLLING_ATTEMPTS
    );
  }, [data, noOfAttempts]);

  useInterval(checkForTerminalState, shouldPoll ? DST_POLLING_INTERVAL : null);

  return { key, dstData: data, noOfAttempts, ...rest };
}

export default useDst;
